import { useAtom, useAtomValue } from 'jotai';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@rocco/components/dialog';
import { Drawer, DrawerContent } from '@rocco/components/drawer';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { cn } from '@rocco/utils/cn';

import type { LeadFormCopy } from '../types';
import { LeadForm } from './LeadForm';
import { leadModalOpenAtom } from './useLeadModal';

export interface LeadModalProps {
    copy: LeadFormCopy;
    /** Resolved path for the left-panel image (Figma `521:28103` export — the
     * woman portrait + "Apply to Join" headline + subtitle are baked into this
     * PNG so we don't render a code overlay on top of it). */
    imageSrc?: string;
    /** Optional logomark image. When the modal image already includes the
     * logomark baked in (current Figma export does), this is unused. */
    logomarkSrc?: string;
    /** Subtitle text — kept for accessibility (rendered via `sr-only`
     * DialogDescription) and for the mobile drawer fallback where the photo
     * is short and the text overlay is rendered in code. */
    subtitle?: string;
}

/**
 * Modal-mode lead form. Opened from any CTA via `useLeadModal().openModal()`.
 *
 * Desktop: 949.53 × 900 split dialog (Figma `521:28038`) — left photo panel
 * (`521:28103` export, includes the "Apply to Join" headline + subtitle baked
 * into the image), right `#eaeaea` form panel with body blurb at the spec
 * (x=57.66, y=61.24) + form anchored at (x=36.66, y=178) + submit pill inside.
 *
 * Mobile: bottom drawer that stacks a 280-px-tall photo header above the form
 * panel. The headline + subtitle re-render in code on mobile because the
 * photo is cropped too tightly for the baked-in text to read.
 *
 * Rendered once by `B2BLandingPage`.
 */
export const LeadModal = ({
    copy,
    imageSrc,
    logomarkSrc,
    subtitle,
}: LeadModalProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const [isOpen, setIsOpen] = useAtom(leadModalOpenAtom);

    const headline = copy.heading;
    const blurb = copy.modalBlurb;

    /* The right-hand form panel. Desktop sizing in vw against the 1920
       design frame (`px / 19.2 = vw`) so the modal scales with the viewport
       just like the rest of the B2B pages — was previously in `rem` (fixed
       at 16 px because `B2BLandingPage` forces `html { font-size: 16px }`),
       which left the modal stuck at full Figma size while everything else
       around it shrank on narrower desktop widths. */
    const formPanel = (
        <div
            className={cn(
                'relative flex flex-col bg-[#eaeaea] backdrop-blur-[4.926px]',
                'md:h-[46.872vw] md:w-[24.728vw] md:overflow-y-auto md:rounded-br-[0.587vw] md:rounded-tr-[0.587vw]',
            )}
        >
            <p
                className={cn(
                    'text-center font-sans text-black',
                    'md:absolute md:left-[3.003vw] md:top-[3.190vw] md:w-[18.722vw]',
                    'md:text-[0.833vw] md:leading-[1.3]',
                    'max-md:mx-auto max-md:mt-6 max-md:max-w-md max-md:px-6 max-md:text-center max-md:text-[0.938rem] max-md:leading-[1.35]',
                )}
            >
                {blurb}
            </p>

            <div
                className={cn(
                    // `md:pb-[2vw]` gives the submit pill breathing room
                    // above the panel's rounded bottom edge — otherwise the
                    // pill sits flush against the chrome.
                    'md:absolute md:left-[1.909vw] md:top-[9.271vw] md:w-[20.938vw] md:pb-[2vw]',
                    'max-md:px-6 max-md:pb-8 max-md:pt-6',
                )}
            >
                <LeadForm
                    variant="modal"
                    copy={copy}
                    onSuccess={() => undefined}
                />
            </div>
        </div>
    );

    /* The left-hand photo panel — fixed 474.765 × 899.949 on desktop.
       Photo includes "Apply to Join" headline + subtitle baked in; the
       DialogTitle / DialogDescription nodes stay in the DOM as sr-only so
       screen readers still announce them. Mobile re-renders the text in
       code on top of a cropped header photo. */
    const photoPanel = imageSrc && (
        <div
            className={cn(
                'relative overflow-hidden',
                'md:h-[46.872vw] md:w-[24.728vw] md:rounded-bl-[0.587vw] md:rounded-tl-[0.587vw]',
                'max-md:h-[17.5rem] max-md:w-full',
            )}
        >
            <img
                src={imageSrc}
                alt=""
                className="absolute inset-0 size-full object-cover max-md:object-[center_30%]"
            />

            {/* ArchiPro "A" logomark — Figma `521:28107` at (x=15.05, y=48.26)
             *  with size 144.45 × 109.21 in the parent's coords. Sits OUTSIDE
             *  the panel's image fill, which is why the Figma-export PNG
             *  didn't include it. Re-rendered here on top of the photo.
             *  SVG uses `preserveAspectRatio="none"` so both dims are set. */}
            {logomarkSrc && (
                <img
                    src={logomarkSrc}
                    alt=""
                    aria-hidden
                    className={cn(
                        'absolute z-10',
                        'md:left-[0.784vw] md:top-[2.513vw] md:h-[5.688vw] md:w-[7.523vw]',
                        'max-md:left-4 max-md:top-4 max-md:h-12 max-md:w-16',
                    )}
                />
            )}

            <DialogTitle className="sr-only">{headline}</DialogTitle>
            {subtitle && (
                <DialogDescription className="sr-only">
                    {subtitle}
                </DialogDescription>
            )}

            {/* Mobile-only overlay so the cropped header still reads.
                Desktop hides this — the baked-in text in the photo carries
                the visual. */}
            <div className="absolute inset-0 flex flex-col items-center justify-end gap-2 px-6 pb-6 text-center text-white md:hidden">
                <div className="absolute inset-0 bg-black/30" />
                <p className="relative font-twk-ghost text-4xl leading-[1.1] tracking-[-0.03em]">
                    {headline}
                </p>
                {subtitle && (
                    <p className="relative font-twk-ghost text-base leading-[1.2] tracking-[-0.03em]">
                        {subtitle}
                    </p>
                )}
            </div>
        </div>
    );

    if (isMobile) {
        return (
            <Drawer open={isOpen} onOpenChange={setIsOpen} direction="bottom">
                <DrawerContent
                    variant="default"
                    overlayVariant="dark"
                    // Drag-down-to-dismiss via Vaul's built-in gesture +
                    // visible handle pill at the top. No close X — the
                    // floating X overlapped scrolled body text, and the
                    // drag handle plus tap-outside-to-close are clearer
                    // patterns on mobile.
                    hideDefaultCloseButton
                    className="data-[vaul-drawer-direction=bottom]:h-[calc(100vh-4rem)] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:bg-[#eaeaea]"
                >
                    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                        {photoPanel}
                        {formPanel}
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent
                variant="hde"
                closeButton
                closeButtonVariant="roundedSmall"
                className={cn(
                    '!flex flex-row gap-0 overflow-hidden border-0 p-0',
                    // 1920 design frame → vw: 900 / 19.2 = 46.875, 949.54 /
                    // 19.2 = 49.455, 11.27 / 19.2 = 0.587.
                    'left-1/2 top-1/2 h-[46.875vw] max-h-[46.875vw] w-[49.455vw] max-w-[49.455vw]',
                    '-translate-x-1/2 -translate-y-1/2 !rounded-[0.587vw]',
                )}
            >
                {photoPanel}
                {formPanel}
            </DialogContent>
        </Dialog>
    );
};
